import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type {
  OrgMemberRole,
  SessionResponse,
  TenantOrgPermission,
} from '@nadoutong/contracts';
import { PlatformApiError, platformApiRequest } from '../api/client.ts';

/**
 * console-web tenant session context (forked local, epic #897 PR 10 —
 * the last runtime module lifted out of `@nadoutong/console-shared`;
 * with this file console-web imports nothing from the shared factory).
 *
 * Single source of truth for "am I logged in, and as whom". The
 * provider runs one `GET /v1/auth/session` probe at mount to hydrate.
 * Login and verify flows may re-probe `/v1/auth/session` after minting
 * a cookie; this provider remains the single in-memory session mirror.
 *
 * The cookie itself lives in the browser jar; this context only
 * mirrors what the server told us. `clearUser` nulls the mirror AFTER
 * the caller has successfully POSTed /logout.
 *
 * Status values:
 *   - 'loading'          — initial probe pending
 *   - 'authenticated'    — /session returned 200, `user` populated
 *   - 'unauthenticated'  — /session returned 401 or network error
 */
export type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface SessionUser {
  accountId: string;
  orgId: string;
  email: string;
  emailVerified: boolean;
  orgRole: OrgMemberRole;
  permissions: readonly TenantOrgPermission[];
}

export interface SessionContextValue {
  status: SessionStatus;
  user: SessionUser | null;
  setUser: (user: SessionUser) => void;
  clearUser: () => void;
  refresh: () => Promise<void>;
  /**
   * The fetch implementation used for every platform-api call made by
   * consumers of this provider. Production callers get `window.fetch`;
   * tests inject a stub.
   */
  fetchImpl: typeof fetch;
}

const SessionContext = createContext<SessionContextValue | null>(null);

const MANAGER_PERMISSIONS: readonly TenantOrgPermission[] = [
  'org:members:read',
  'org:members:manage',
  'org:invitations:manage',
  'org:api_keys:read',
  'org:api_keys:manage',
  'member:api_keys:manage',
  'org:billing:read',
  'org:billing:manage',
  'org:usage:read',
  'member:usage:read',
  'org:audit:read',
  'member:audit:read',
  'org:reports:export',
  'member:models:read',
  'member:monthly_limit:read',
  'member:requests:read',
];

const MEMBER_PERMISSIONS: readonly TenantOrgPermission[] = [
  'member:api_keys:manage',
  'member:usage:read',
  'member:audit:read',
  'member:models:read',
  'member:monthly_limit:read',
  'member:requests:read',
];

export function permissionsForRole(role: OrgMemberRole): readonly TenantOrgPermission[] {
  return role === 'member' ? MEMBER_PERMISSIONS : MANAGER_PERMISSIONS;
}

export function hasPermission(
  user: SessionUser | null,
  permission: TenantOrgPermission,
): boolean {
  return user?.permissions.includes(permission) === true;
}

function normaliseRole(raw: unknown): OrgMemberRole {
  return raw === 'owner' || raw === 'admin' || raw === 'member' ? raw : 'owner';
}

function normalisePermissions(
  raw: unknown,
  role: OrgMemberRole,
): readonly TenantOrgPermission[] {
  if (!Array.isArray(raw)) {
    return permissionsForRole(role);
  }
  return raw.filter((item): item is TenantOrgPermission => (
    item === 'org:members:read' ||
    item === 'org:members:manage' ||
    item === 'org:invitations:manage' ||
    item === 'org:api_keys:read' ||
    item === 'org:api_keys:manage' ||
    item === 'member:api_keys:manage' ||
    item === 'org:billing:read' ||
    item === 'org:billing:manage' ||
    item === 'org:usage:read' ||
    item === 'member:usage:read' ||
    item === 'org:audit:read' ||
    item === 'member:audit:read' ||
    item === 'org:reports:export' ||
    item === 'member:models:read' ||
    item === 'member:monthly_limit:read' ||
    item === 'member:requests:read'
  ));
}

export function sessionUserFromResponse(
  session: Pick<SessionResponse, 'accountId' | 'orgId' | 'email' | 'emailVerified'> &
    Partial<Pick<SessionResponse, 'orgRole' | 'permissions'>>,
): SessionUser {
  const orgRole = normaliseRole(session.orgRole);
  return {
    accountId: session.accountId,
    orgId: session.orgId,
    email: session.email,
    emailVerified: session.emailVerified,
    orgRole,
    permissions: normalisePermissions(session.permissions, orgRole),
  };
}

interface ProviderProps {
  children: ReactNode;
  /**
   * Injection point for tests. Production callers leave undefined and
   * the provider falls back to `window.fetch`.
   */
  fetchImpl?: typeof fetch;
}

export function SessionProvider({ children, fetchImpl }: ProviderProps): JSX.Element {
  const resolvedFetch: typeof fetch = useMemo(
    () => fetchImpl ?? ((...args) => fetch(...args)),
    [fetchImpl],
  );
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [user, setUserState] = useState<SessionUser | null>(null);

  const refresh = useCallback(async () => {
    setStatus('loading');
    try {
      const session = await platformApiRequest<SessionResponse>(
        '/v1/auth/session',
        {},
        resolvedFetch,
      );
      setUserState(sessionUserFromResponse(session));
      setStatus('authenticated');
    } catch (err) {
      if (err instanceof PlatformApiError && err.status === 401) {
        setUserState(null);
        setStatus('unauthenticated');
        return;
      }
      // Network / 5xx errors: fail closed so `RequireAuth` redirects
      // to /login. A dedicated "service down" banner is a follow-up
      // polish item.
      setUserState(null);
      setStatus('unauthenticated');
    }
  }, [resolvedFetch]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setUser = useCallback((next: SessionUser) => {
    setUserState(next);
    setStatus('authenticated');
  }, []);

  const clearUser = useCallback(() => {
    setUserState(null);
    setStatus('unauthenticated');
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({ status, user, setUser, clearUser, refresh, fetchImpl: resolvedFetch }),
    [status, user, setUser, clearUser, refresh, resolvedFetch],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (ctx === null) {
    throw new Error('useSession must be used inside <SessionProvider>');
  }
  return ctx;
}
